import {useMutation, useQueryClient, type UseMutationOptions, type UseMutationResult} from 'react-query';
import type {AxiosError} from 'axios';
import {hideBanner} from '../../http/banners/hideBanner';
import type {FetchBannersResponse} from '../../http/banners/fetchBanners';
import {bannersQueryKey} from './useFetchBanners';

type HideBannerContext = {
	previous?: FetchBannersResponse;
};

export function useHideBanner(
	options?: UseMutationOptions<void, AxiosError, number, HideBannerContext>,
): UseMutationResult<void, AxiosError, number, HideBannerContext> {
	const queryClient = useQueryClient();

	return useMutation<void, AxiosError, number, HideBannerContext>((id) => hideBanner({id}).then(() => undefined), {
		...options,
		onMutate: async (id) => {
			await queryClient.cancelQueries(bannersQueryKey);
			const previous = queryClient.getQueryData<FetchBannersResponse>(bannersQueryKey);

			queryClient.setQueryData<FetchBannersResponse | undefined>(bannersQueryKey, (current) => {
				if (!current) return current;

				return {
					...current,
					data: current.data?.filter((banner) => banner.id !== id),
				};
			});

			const context = {previous};
			await options?.onMutate?.(id);

			return context;
		},
		onError: (error, id, context) => {
			if (context?.previous) {
				queryClient.setQueryData(bannersQueryKey, context.previous);
			}

			options?.onError?.(error, id, context);
		},
		onSuccess: (data, id, context) => {
			options?.onSuccess?.(data, id, context);
		},
		onSettled: (data, error, id, context) => {
			void queryClient.invalidateQueries(bannersQueryKey);
			options?.onSettled?.(data, error, id, context);
		},
	});
}
